import { Loading } from '@nike/frame-component-library';
import PropTypes from 'prop-types';
import React from 'react';

import { unPadStoreNumber } from '../../../utils/formatting';
import { scsGet, scsDefinitionGet, scsBulkedit } from '../../../utils/service-calls/scs';
import { toGeo } from '../../../utils/static/scs-geo-iso-map';
import { StoreSelect, ButtonBlack } from '../../reusable';

const config = require('../../../config')();

const ALL_REGIONS = config.scsRegionOptions || [];

const KEY_GROUP_FILTERS = [
  { label: 'All Keys', value: 'ALL' },
  { label: 'NPOS Fixed', value: 'NPOS_FIXED' },
  { label: 'NPOS Mobile', value: 'NPOS_MOBILE' },
];

const NPOS_FIXED_KEYS = [
  'NPOS.EnableMixedCart',
  'NPOS.NewRelicMaxBufferTime',
  'NPOS.NewRelicMaxPoolSize',
  'NPOS.ScanDigitsToRemove',
  'NPOS.ScanModifyPosition',
  'NPOS.appMinimumVersionAndForcedUpdateDate',
  'NPOS.appStartupFetchTimeoutInterval',
  'NPOS.approvedNFCReaderPackage',
  'NPOS.cfdMemberQRHeadlineText',
  'NPOS.cfdMinimumVersionAndForcedUpdateDate',
  'NPOS.coachGatedUnverifiedReturnMinimumSecurityLevel',
  'NPOS.defaultAmountDiscounts',
  'NPOS.defaultPercentageDiscounts',
  'NPOS.giftCardCashOutMaxAmount',
  'NPOS.giftCardDenominations',
  'NPOS.isAdyenCardEnabled',
  'NPOS.isAdyenNYC1Enabled',
  'NPOS.isAllNonMerchEnabled',
  'NPOS.isAthleteFeedbackEnabled',
  'NPOS.isBagFeePopupToBeShown',
  'NPOS.isCFDLanguageSwitchEnabled',
  'NPOS.isCashEnabled',
  'NPOS.isCashLoanEnabled',
  'NPOS.isComplimentaryTenderEnabled',
  'NPOS.isConsumerDisplayEnabled',
  'NPOS.isConsumerInformationRequired',
  'NPOS.isControlTransactionEnabled',
  'NPOS.isCouponEnabled',
  'NPOS.isDeviceSessionManagementEnabled',
  'NPOS.isDiscountEnabled',
  'NPOS.isEmbellishmentsEnabled',
  'NPOS.isExchangeEnabled',
  'NPOS.isFiservCardEnabled',
  'NPOS.isFootwearAccessoriesEnabled',
  'NPOS.isForeignCashEuroEnabled',
  'NPOS.isFranceComplianceForIpadEnabled',
  'NPOS.isGiftCardCashOutEnabled',
  'NPOS.isGiftCardEnabled',
  'NPOS.isGiftCardSaleEnabled',
  'NPOS.isGlobalBlueEnabled',
  'NPOS.isItemNotOnFileEnabled',
  'NPOS.isKoreaNonIntegratedCardEnabled',
  'NPOS.isLanguageSwitchingOptionEnabled',
  'NPOS.isMallCertificateEnabled',
  'NPOS.isManagerApprovalEnabled',
  'NPOS.isManualPassDiscountEnabled',
  'NPOS.isMaxAmountDiscountAllowed',
  'NPOS.isMemberLookupEnabled',
  'NPOS.isMemberTokenQREnabled',
  'NPOS.isMixedCartEnabled',
  'NPOS.isMultiRegionEnabled',
  'NPOS.isMultipeerHeartbeatEnabled',
  'NPOS.isNFCEnabled',
  'NPOS.isNetworkP2PEnabled',
  'NPOS.isNikeByYouEnabled',
  'NPOS.isNonIntegratedCardEnabled',
  'NPOS.isNonIntegratedCashEnabled',
  'NPOS.isOfflineAdyenCardEnabled',
  'NPOS.isOfflineHealthCheckEnabled',
  'NPOS.isOfflineModeEnabled',
  'NPOS.isOfflineProductListNewFormatEnabled',
  'NPOS.isOfflineTestEnabled',
  'NPOS.isOnlineRoundingRulesEnabled',
  'NPOS.isPartialReturnsDisabled',
  'NPOS.isPassPlayEnabled',
  'NPOS.isPickupsEnabled',
  'NPOS.isPostPurchaseInvoiceEnabled',
  'NPOS.isPriceOverrideEnabled',
  'NPOS.isPriceOverrideManagerApprovalEnabled',
  'NPOS.isProductQRScanEnabled',
  'NPOS.isReceiptGenerationEnabled',
  'NPOS.isReceiptReprintEnabled',
  'NPOS.isRegisterLevelTrackingEnabled',
  'NPOS.isRepairEnabled',
  'NPOS.isReturnEnabled',
  'NPOS.isScanOptimizationEnabled',
  'NPOS.isSuspendAndResumeEnabled',
  'NPOS.isUnverifiedReturnsEnabled',
  'NPOS.isWiredEthernetP2PConnectionDisabled',
  'NPOS.isZeroPriceBagFeeEnabled',
  'NPOS.kioskLinksPollingInterval',
  'NPOS.maxPercentDiscountAllowed',
  'NPOS.memberCreationQRURL',
  'NPOS.networkTimeoutInterval',
  'NPOS.offlineInvoiceNumberEnabled',
  'NPOS.offlineOrderNumbersUpdateThreshold',
  'NPOS.offlineProductListNewFormatURL',
  'NPOS.offlineReceiptFooterUpdateIntervalHours',
  'NPOS.receiptOptionsAllowed',
  'NPOS.storeTaxID',
  'NPOS.testOpening',
];

const NPOS_MOBILE_KEYS = [
  'Assist.CreateMemberEnabled',
  'Assist.EnableDiscountUIOption',
  'Assist.EnableMixedCart',
  'Assist.IsCoachsViewEnabled',
  'Assist.IsGiftItemAllowed',
  'Assist.IsMaximumAmountDiscountAllowed',
  'Assist.IsNearbyStoreInventoryEnabled',
  'Assist.IsPassPlayEnabled',
  'Assist.IsPriceoverrideAllowed',
  'Assist.IsRetailMembershipPodReturnsEnabled',
  'Assist.IsReturnsEnabled',
  'Assist.IsTaxIDEnabled',
  'Assist.MaximumPercentDiscountAllowed',
  'Assist.MembershipTabEnabled',
  'Assist.NewRelicMaxBufferTime',
  'Assist.NewRelicMaxPoolSize',
  'Assist.PartnerName',
  'Assist.PrivacyPolicyURL',
  'Assist.ProductViewsServiceUrl',
  'Assist.appStartupFetchTimeoutInterval',
  'Assist.cashDrawerTTLSeconds',
  'Assist.defaultAmountDiscounts',
  'Assist.defaultPercentageDiscounts',
  'Assist.devicePlacementAngleVariance',
  'Assist.enableMemberUnlocks',
  'Assist.giftCardDenominations',
  'Assist.isAddressValidationEnabled',
  'Assist.isAdyenCardEnabled',
  'Assist.isAdyenNYC1Enabled',
  'Assist.isAdyenTTPEnabled',
  'Assist.isAllNonMerchEnabled',
  'Assist.isAllowZeroPrices',
  'Assist.isAppointmentsEnabled',
  'Assist.isAssociateLinkingEnabled',
  'Assist.isAthleteFeedbackEnabled',
  'Assist.isAustraliaMarketplaceOverrideEnabled',
  'Assist.isBagFeePopupToBeShown',
  'Assist.isComplimentaryTenderEnabled',
  'Assist.isConsumerInformationRequired',
  'Assist.isDeviceSessionManagementEnabled',
  'Assist.isEmbellishmentsEnabled',
  'Assist.isEventCheckinEnabled',
  'Assist.isExchangeEnabled',
  'Assist.isFiservCardEnabled',
  'Assist.isFootwearAccessoriesEnabled',
  'Assist.isGiftCardEnabled',
  'Assist.isGiftCardSaleEnabled',
  'Assist.isIndiaNonIntegratedUPIEnabled',
  'Assist.isKoreaNonIntegratedCardEnabled',
  'Assist.isLanguageSwitchingOptionEnabled',
  'Assist.isLotteryCodeEnabled',
  'Assist.isMallCertificateEnabled',
  'Assist.isManualPassDiscountEnabled',
  'Assist.isMemberOrderHistoryEnabled',
  'Assist.isMemberPickupEnabled',
  'Assist.isMemberTokenQREnabled',
  'Assist.isMultiRegionEnabled',
  'Assist.isNFCEnabled',
  'Assist.isNRCQREnabled',
  'Assist.isNTCQREnabled',
  'Assist.isNikeAppQREnabled',
  'Assist.isNikeByYouEnabled',
  'Assist.isNonIntegratedCardEnabled',
  'Assist.isNonIntegratedCashEnabled',
  'Assist.isOfflineNonIntegratedCardEnabled',
  'Assist.isOfflineNonIntegratedUPIEnabled',
  'Assist.isPassPlayEnabled',
  'Assist.isPickupsEnabled',
  'Assist.isProductQRScanEnabled',
  'Assist.isPromotionOverrideEnabled',
  'Assist.isReceiptGenerationEnabled',
  'Assist.isReceiptReprintEnabled',
  'Assist.isRecyclingAndDonationApparelEnabled',
  'Assist.isRecyclingAndDonationFootwearEnabled',
  'Assist.isRegisterLevelTrackingEnabled',
  'Assist.isRepairEnabled',
  'Assist.isReturnEnabled',
  'Assist.isReuseAShoeEnabled',
  'Assist.isSNKRSQREnabled',
  'Assist.isScanOptimizationEnabled',
  'Assist.isSearchDisabled',
  'Assist.isSecuredCashRefundEnabled',
  'Assist.isSerialPrinterDiscoveryEnabled',
  'Assist.isSpeechToTextEnabled',
  'Assist.isTrialZoneServiceEnabled',
  'Assist.isUnreferencedAdyenRefundEnabled',
  'Assist.isZeroPriceBagFeeEnabled',
  'Assist.isadyenCardEnabled',
  'Assist.kioskLinksPollingInterval',
  'Assist.maxNoBarcodeDetectedDuration',
  'Assist.memberCreationQRURL',
  'Assist.pauseCartItemScanMilliseconds',
  'Assist.receiptOptionsAllowed',
  'Assist.scanConfidence',
  'Assist.suppressAthleteDetailsFromViewCompleted',
  'Assist.testOpening',
];

const COMMON_KEYS = [
  'POS.GRR.Footer.Static',
  'POS.GRR.Footer.Survey',
  'POS.GRR.Footer.Params',
  'POS.STV.StoreVATNumber',
  'promote.allowPromoteApp',
  'rise.offlineProductFeedEnabled',
  'true.disableEJ',
];

const ALLOWED_KEYS = new Set([...NPOS_FIXED_KEYS, ...NPOS_MOBILE_KEYS, ...COMMON_KEYS]);
const NPOS_FIXED_SET = new Set(NPOS_FIXED_KEYS);
const NPOS_MOBILE_SET = new Set(NPOS_MOBILE_KEYS);
const COMMON_SET = new Set(COMMON_KEYS);

// Non-breaking spaces used as the placeholder for empty value cells so the
// blank box keeps a reasonable width across Quick Edit, Compare, and Copy
// flows instead of collapsing to a sliver.
const EMPTY_VALUE_PLACEHOLDER = '\u00A0'.repeat(20);

class StoreConfigTab extends React.Component {
  constructor(props) {
    super(props);
    this.panelRef = React.createRef();
    this.regionTabsRef = React.createRef();
    this.tableHeadRef = React.createRef();
    this.state = {
      approvals: {},
      collapsedGroups: {},
      fetching: false,
      hoveredKeyInfo: null,
      keySearch: '',
      loadFailed: false,
      mode: 'QUICK_EDIT',
      originalTargetSnapshots: {},
      rowCommitted: {},
      rowEditBackup: {},
      rowEditMode: {},
      saveStatus: null,
      saving: false,
      selectedKeyGroup: 'ALL',
      selectedRegion: 'use1',
      selectedSourceStore: null,
      selectedTargetStore: null,
      showOnlyApprovable: false,
      showOnlyDifferences: false,
      sourceConfigs: {},
      sourceStoreKey: '',
      targetConfigs: {},
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateStickyOffsets);
    this.attachResizeObserver();
    this.updateStickyOffsets();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedCountry?.value !== this.props.selectedCountry?.value) {
      this.resetForCountryChange();
    }
    // Re-attach the observer in case the panel/thead/etc were remounted
    // (e.g., hasConfigs flipped false -> true) and recompute the offsets
    // so pinned bands align against the current rendered heights.
    this.attachResizeObserver();
    this.updateStickyOffsets();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateStickyOffsets);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  // Watch the sticky-band elements so tab-wrap, font load, or content
  // changes trigger a recompute even without a React re-render.
  attachResizeObserver = () => {
    if (typeof ResizeObserver === 'undefined') return;
    const targets = [
      this.regionTabsRef.current,
      this.tableHeadRef.current,
    ].filter(Boolean);
    if (targets.length === 0) return;

    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.resizeObserver = new ResizeObserver(this.updateStickyOffsets);
    targets.forEach((el) => this.resizeObserver.observe(el));
  };

  // Measure real rendered heights of each sticky band and push them into
  // CSS custom properties on the scroll container. The stacked sticky
  // rules in onboarding.css add these up to compute their `top` offsets,
  // so this keeps every pinned band flush against the one above it
  // (no gaps, no overlap covering the current group header).
  //
  // We use getBoundingClientRect().height (fractional) and Math.floor to
  // ensure the reported height is never greater than the actual rendered
  // height. That guarantees each subsequent sticky band is placed slightly
  // *inside* the previous band rather than after a subpixel gap — which
  // accidentally writing an empty STORE row.
buildCopyTargetConfig = (sourceItems, targetItems, defMap) => {
    const sourceByKey = new Map(sourceItems.map((i) => [i.key, i]));
    const targetByKey = new Map(targetItems.map((i) => [i.key, i]));
    return [...ALLOWED_KEYS].map((key) => {
      if (!defMap[key]) {
        return this.buildMissingDefRow(key);
      }
      // `missingFromTarget` reflects the target store's own SCS state and is
      // computed independently of what the source provides — a key can be
      // cloned in from the source and still be flagged as missing on the
      // target so users know they're introducing a new value there.
      const targetHasValue = targetByKey.has(key);
      const fromSource = sourceByKey.get(key);
      if (fromSource && fromSource.scopeType === 'STORE') {
        return {
          ...fromSource,
          missingFromDefinition: false,
          missingFromTarget: !targetHasValue,
          scope: 'STORE',
          scopeType: 'STORE',
        };
      }
      const fromTarget = targetByKey.get(key);
      if (fromTarget) {
        return { ...fromTarget, missingFromDefinition: false, missingFromTarget: false };
      }
      return {
        description: defMap[key]?.description || '',
        group: defMap[key]?.group || 'Other Settings',
        key,
        missingFromDefinition: false,
        missingFromTarget: true,
        scope: 'STORE',
        scopeType: 'STORE',
        value: '',
        valueType: defMap[key]?.type || 'STRING',
      };
    }).sort((a, b) => a.key.localeCompare(b.key));
  };

// intentionally read-only and non-approvable (see isEditable / isRowApprovable).
buildMissingDefRow = (key) => ({
    description: '',
    group: 'Missing Definitions',
    key,
    missingFromDefinition: true,
    missingFromTarget: false,
    scope: 'STORE',
    scopeType: 'STORE',
    value: '',
    valueType: 'STRING',
  });

buildOriginalSnapshots = (targetConfigsByRegion) => {
    const snapshots = {};
    Object.entries(targetConfigsByRegion).forEach(([region, items]) => {
      items.forEach((item) => {
        snapshots[`${region}-${item.key}`] = {
          scope: item.scope,
          scopeType: item.scopeType,
          value: item.value,
        };
      });
    });
    return snapshots;
  };

// key but the region's definition service is not aware of it. The row is
buildTargetFromAllowed = (fetchedForRegion, defMap) => {
    const byKey = new Map(fetchedForRegion.map((i) => [i.key, i]));
    return [...ALLOWED_KEYS].map((key) => {
      if (!defMap[key]) {
        return this.buildMissingDefRow(key);
      }
      const existing = byKey.get(key);
      if (existing) {
        return { ...existing, missingFromDefinition: false, missingFromTarget: false };
      }
      return {
        description: defMap[key]?.description || '',
        group: defMap[key]?.group || 'Other Settings',
        key,
        missingFromDefinition: false,
        missingFromTarget: true,
        scope: 'STORE',
        scopeType: 'STORE',
        value: '',
        valueType: defMap[key]?.type || 'STRING',
      };
    }).sort((a, b) => a.key.localeCompare(b.key));
  };

// back to the target list from ALLOWED_KEYS in the build helpers above.
enrichFetched = (data, defMap) => data
    .filter((item) => ALLOWED_KEYS.has(item.key) && !!defMap[item.key])
    .map((item) => ({
      ...item,
      description: defMap[item.key]?.description || '',
      group: defMap[item.key]?.group || 'Other Settings',
      missingFromDefinition: false,
      missingFromTarget: false,
      valueType: defMap[item.key]?.type || 'STRING',
    }))
    .sort((a, b) => a.key.localeCompare(b.key));

// definitions and others silently degrade to Missing Definitions.
fetchDefinitions = async () => {
    const results = await Promise.all(
      ALL_REGIONS.map((region) => scsDefinitionGet(this.props.userInfo.accessToken, region.value)
        .then((res) => ({ data: res.body.objects || [], region: region.value }))),
    );
    const defMapByRegion = {};
    results.forEach(({ region, data }) => {
      const regionMap = {};
      data.forEach((d) => {
        if (!regionMap[d.key]) regionMap[d.key] = d;
      });
      defMapByRegion[region] = regionMap;
    });
    return defMapByRegion;
  };

// Fail-all: any per-region rejection propagates and blocks the whole load.
fetchRegionConfigsForStore = async (storeKey) => Promise.all(
    ALL_REGIONS.map((region) => scsGet(this.props.userInfo.accessToken, storeKey, region.value)
      .then((res) => ({ data: res.body.objects || [], region: region.value }))),
  );

// or a whitespace-only string), we fall back to 'Other Settings' — the
formatStoreForHeader = (store) => {
    if (!store) return null;
    const country = store.address?.country || '';
    const number = unPadStoreNumber(store.storeNumber);
    const metaParts = [number && `#${number}`, country].filter(Boolean);
    return {
      key: `${country}-${store.storeNumber}`,
      meta: metaParts.join(' \u00B7 '),
      name: store.name || store.label || `Store ${number}`,
    };
  };

// want to render a partial view where some regions have their real
getAllApproved = () => {
    const { targetConfigs, approvals } = this.state;
    const regionKeys = Object.keys(targetConfigs);
    if (regionKeys.length === 0) return false;

    let hasApprovable = false;
    let allApproved = true;
    regionKeys.forEach((regionValue) => {
      const regionConfig = targetConfigs[regionValue] || [];
      regionConfig.forEach((item) => {
        if (!this.isRowApprovable(regionValue, item)) return;
        hasApprovable = true;
        if (!approvals[`${regionValue}-${item.key}`]) {
          allApproved = false;
        }
      });
    });
    return hasApprovable && allApproved;
  };

// payload down to approved + STORE-scoped rows, so partial approval is
getFilteredConfigs = (configs, {
    applyApprovableFilter = false, applyDifferencesFilter = false, skipSearch = false,
  } = {}) => {
    const {
      keySearch, mode, selectedKeyGroup, selectedRegion,
      showOnlyApprovable, showOnlyDifferences,
    } = this.state;
    const filtered = {};

    Object.entries(configs).forEach(([region, items]) => {
      let result = items;

      if (selectedKeyGroup === 'NPOS_FIXED') {
        result = result.filter((item) => NPOS_FIXED_SET.has(item.key) || COMMON_SET.has(item.key));
      } else if (selectedKeyGroup === 'NPOS_MOBILE') {
        result = result.filter((item) => NPOS_MOBILE_SET.has(item.key) || COMMON_SET.has(item.key));
      }

      if (!skipSearch && keySearch) {
        const regex = new RegExp(keySearch.trim(), 'i');
        result = result.filter((item) => regex.test(item.key));
      }

      // "Show only approvable keys" narrows the table down to rows the user
      // can actually act on — i.e., rows that participate in the approval
      // gate for Save. Non-approvable rows (missing-def, and non-STORE rows
      // whose scope change hasn't been committed yet) are hidden. Applied
      // only for the currently visible region and only against the target
      // config list, so region tabs and source-column context keep their
      // full data.
      if (applyApprovableFilter && showOnlyApprovable && region === selectedRegion) {
        result = result.filter((item) => this.isRowApprovable(region, item));
      }

      // "Show only differences" is only meaningful in COMPARE — it hides
      // rows where the reference-store value matches the target-store value
      // so the user can focus on drift. Same region-scoping as the
      // approvable filter above: only narrows the currently visible region
      // on the target config list.
      if (
        applyDifferencesFilter
        && showOnlyDifferences
        && mode === 'COMPARE'
        && region === selectedRegion
      ) {
        result = result.filter((item) => this.isRowDifferent(region, item));
      }

      filtered[region] = result;
    });

    return filtered;
  };

// default bucket for keys without an explicit group.
getGroupedTargetConfig = (items) => {
    const groupMap = new Map();
    items.forEach((item) => {
      const raw = typeof item.group === 'string' ? item.group.trim() : '';
      const groupName = raw || 'Other Settings';
      if (!groupMap.has(groupName)) groupMap.set(groupName, []);
      groupMap.get(groupName).push(item);
    });
    const priority = (name) => {
      if (name === 'Missing Definitions') return 2;
      if (name === 'Other Settings') return 1;
      return 0;
    };
    return [...groupMap.entries()].sort(([a], [b]) => {
      const diff = priority(a) - priority(b);
      if (diff !== 0) return diff;
      return a.localeCompare(b);
    });
  };

// If a key's definition doesn't specify a group (missing, null, non-string,
getSelectorLabels = () => {
    const { mode } = this.state;
    if (mode === 'COPY') {
      return { source: 'Source', target: 'Target' };
    }
    if (mode === 'QUICK_EDIT') {
      return { source: null, target: 'Store to Edit' };
    }
    return { source: 'Reference Store', target: 'Store to Edit' };
  };

//
getSourceColumnLabel = () => {
    const { mode } = this.state;
    if (mode === 'COPY') return 'Original Config';
    if (mode === 'COMPARE') return 'Reference Config';
    return '';
  };

// catch-alls, and everything else is sorted alphabetically.
getTargetColumnLabel = () => {
    const { mode } = this.state;
    if (mode === 'COPY') return 'New Config';
    return 'Config';
  };

// has been approved. handleSave already filters the per-region bulk
handleApprovalChange = (regionValue, key) => {
    this.setState((prevState) => ({
      approvals: {
        ...prevState.approvals,
        [`${regionValue}-${key}`]: !prevState.approvals[`${regionValue}-${key}`],
      },
    }));
  };

// Save is enabled as soon as at least one approvable row (in any region)
handleApproveAllForRegion = (regionValue) => {
    const { targetConfigs, approvals } = this.state;
    const approvable = (targetConfigs[regionValue] || []).filter(
      (item) => this.isRowApprovable(regionValue, item),
    );
    if (approvable.length === 0) return;

    const allApproved = approvable.every(
      (item) => approvals[`${regionValue}-${item.key}`],
    );

    const newApprovals = { ...approvals };
    approvable.forEach((item) => {
      newApprovals[`${regionValue}-${item.key}`] = !allApproved;
    });

    this.setState({ approvals: newApprovals });
  };

// Promise.all rejects and loadConfigs takes the failure path. We never
handleGroupApproveAll = (regionValue, groupItems) => {
    const { approvals } = this.state;
    const approvable = groupItems.filter(
      (item) => this.isRowApprovable(regionValue, item),
    );
    if (approvable.length === 0) return;

    const allApproved = approvable.every(
      (item) => approvals[`${regionValue}-${item.key}`],
    );

    const newApprovals = { ...approvals };
    approvable.forEach((item) => {
      newApprovals[`${regionValue}-${item.key}`] = !allApproved;
    });

    this.setState({ approvals: newApprovals });
  };

// Fail-all: if any single region's definition fetch rejects, the whole
handleKeyHoverEnter = (item, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const TOOLTIP_MAX_WIDTH = 420;
    const HORIZONTAL_MARGIN = 8;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const left = Math.min(rect.left, viewportWidth - TOOLTIP_MAX_WIDTH - HORIZONTAL_MARGIN);
    this.setState({
      hoveredKeyInfo: {
        description: item.description || '',
        key: item.key,
        left: Math.max(HORIZONTAL_MARGIN, left),
        top: rect.bottom + 6,
        valueType: item.valueType || '',
      },
    });
  };

// we drop anything without a defMap entry here. Missing-def keys are added
handleKeyHoverLeave = () => {
    this.setState({ hoveredKeyInfo: null });
  };

// can review or override them via the standard Edit flow instead of
handleRowEditCancel = (regionValue, key) => {
    this.setState((prev) => {
      const flagKey = `${regionValue}-${key}`;
      const regionConfig = [...(prev.targetConfigs[regionValue] || [])];
      const idx = regionConfig.findIndex((i) => i.key === key);
      const backup = prev.rowEditBackup[flagKey];
      const snap = prev.originalTargetSnapshots[flagKey];
      const restoreFrom = backup || snap;
      if (idx !== -1 && restoreFrom) {
        regionConfig[idx] = {
          ...regionConfig[idx],
          scope: restoreFrom.scope,
          scopeType: restoreFrom.scopeType,
          value: restoreFrom.value,
        };
      }
      const wasCommitted = !!(backup && backup.wasCommitted);
      const restoredApproval = !!(backup && backup.approved);
      const newRowEditMode = { ...prev.rowEditMode };
      delete newRowEditMode[flagKey];
      const newRowEditBackup = { ...prev.rowEditBackup };
      delete newRowEditBackup[flagKey];
      const newRowCommitted = { ...prev.rowCommitted };
      if (wasCommitted) {
        newRowCommitted[flagKey] = true;
      } else {
        delete newRowCommitted[flagKey];
      }
      return {
        approvals: { ...prev.approvals, [flagKey]: restoredApproval },
        rowCommitted: newRowCommitted,
        rowEditBackup: newRowEditBackup,
        rowEditMode: newRowEditMode,
        targetConfigs: { ...prev.targetConfigs, [regionValue]: regionConfig },
      };
    });
  };

// non-STORE scope) fall back to the target's real value and scope, so users
handleRowEditDone = (regionValue, key) => {
    this.setState((prev) => {
      const flagKey = `${regionValue}-${key}`;
      const snap = prev.originalTargetSnapshots[flagKey];
      const regionConfig = prev.targetConfigs[regionValue] || [];
      const current = regionConfig.find((i) => i.key === key);
      if (snap && current && String(snap.value) === String(current.value)) {
        return null;
      }
      const newRowEditMode = { ...prev.rowEditMode };
      delete newRowEditMode[flagKey];
      const newRowEditBackup = { ...prev.rowEditBackup };
      delete newRowEditBackup[flagKey];
      return {
        rowCommitted: { ...prev.rowCommitted, [flagKey]: true },
        rowEditBackup: newRowEditBackup,
        rowEditMode: newRowEditMode,
      };
    });
  };

// STORE scope. Keys the source doesn't provide (or provides only at a
handleRowEditStart = (regionValue, key) => {
    this.setState((prev) => {
      const flagKey = `${regionValue}-${key}`;
      const regionConfig = [...(prev.targetConfigs[regionValue] || [])];
      const idx = regionConfig.findIndex((i) => i.key === key);
      const current = idx !== -1 ? regionConfig[idx] : null;
      const wasCommitted = !!prev.rowCommitted[flagKey];
      const backup = current
        ? {
          approved: !!prev.approvals[flagKey],
          scope: current.scope,
          scopeType: current.scopeType,
          value: current.value,
          wasCommitted,
        }
        : null;
      if (idx !== -1) {
        regionConfig[idx] = { ...regionConfig[idx], scope: 'STORE', scopeType: 'STORE' };
      }
      return {
        approvals: { ...prev.approvals, [flagKey]: false },
        rowEditBackup: backup
          ? { ...prev.rowEditBackup, [flagKey]: backup }
          : prev.rowEditBackup,
        rowEditMode: { ...prev.rowEditMode, [flagKey]: true },
        targetConfigs: { ...prev.targetConfigs, [regionValue]: regionConfig },
      };
    });
  };

// Edit toggle re-appears for non-STORE rows, and approval must be re-earned.
handleRowUndo = (regionValue, key) => {
    this.setState((prev) => {
      const flagKey = `${regionValue}-${key}`;
      const snap = prev.originalTargetSnapshots[flagKey];
      if (!snap) return null;
      const regionConfig = [...(prev.targetConfigs[regionValue] || [])];
      const idx = regionConfig.findIndex((i) => i.key === key);
      if (idx === -1) return null;
      regionConfig[idx] = {
        ...regionConfig[idx],
        scope: snap.scope,
        scopeType: snap.scopeType,
        value: snap.value,
      };
      const newRowEditMode = { ...prev.rowEditMode };
      delete newRowEditMode[flagKey];
      const newRowEditBackup = { ...prev.rowEditBackup };
      delete newRowEditBackup[flagKey];
      const newRowCommitted = { ...prev.rowCommitted };
      delete newRowCommitted[flagKey];
      return {
        approvals: { ...prev.approvals, [flagKey]: false },
        rowCommitted: newRowCommitted,
        rowEditBackup: newRowEditBackup,
        rowEditMode: newRowEditMode,
        targetConfigs: { ...prev.targetConfigs, [regionValue]: regionConfig },
      };
    });
  };

// SCS only returns values for keys that have a definition in the region, so
handleSave = async () => {
    const { targetConfigs, approvals, selectedTargetStore } = this.state;
    const { hasNextTab, onSaveAndNext } = this.props;
    const requestedBy = this.props.userInfo?.email || '';
    const country = selectedTargetStore?.address?.country || '';
    const brand = selectedTargetStore?.brand || 'NIKE';
    const geo = selectedTargetStore ? toGeo(selectedTargetStore) : '';
    const targetStoreKey = selectedTargetStore
      ? `${country}-${selectedTargetStore.storeNumber}`
      : '';

    // Bulk-save payload shape mirrors scs/Keyset.js `onSave` (lines 629-636):
    //   { changeRequest: { justification, requestedBy }, data: [...] }
    // where each `data` entry is
    //   { key, scopeType, value, scopeInfo: [{ hierarchy, scope }] }
    // SCS is called per region, so we produce one bulk body per region.
    // Only STORE-scoped rows participate in approval, so any non-STORE
    // item is discarded here — it never makes it into the bulk payload.
    const payloadByRegion = {};
    let totalKeys = 0;
    Object.entries(targetConfigs).forEach(([region, items]) => {
      const approvedItems = items.filter(
        (item) => approvals[`${region}-${item.key}`] && item.scopeType === 'STORE',
      );
      if (approvedItems.length === 0) return;

      const data = approvedItems.map((item) => ({
        key: item.key,
        scopeInfo: [{
          hierarchy: { brand, country, geo },
          scope: targetStoreKey,
        }],
        scopeType: 'STORE',
        value: item.value,
      }));

      payloadByRegion[region] = {
        changeRequest: {
          justification: `${requestedBy || 'User'} has bulk-updated ${approvedItems.length} SCS ${approvedItems.length === 1 ? 'key' : 'keys'} for store ${targetStoreKey}`,
          requestedBy,
        },
        data,
      };
      totalKeys += approvedItems.length;
    });

    const regionCount = Object.keys(payloadByRegion).length;

    if (regionCount === 0) return;

    // Pass all or fail all: fire one scsBulkedit per region in parallel via
    // Promise.all (mirrors `fetchDefinitions` / `fetchRegionConfigsForStore`).
    // If any per-region call rejects, the whole Promise.all rejects and we
    // surface a single save failure. In addition, since bulk edit can report
    // per-item failures inside `response.body.failure`, we treat ANY such
    // failure in ANY region as a fail-all — a save is only considered
    // successful when every region resolves with zero failures.
    this.setState({ saveStatus: null, saving: true });

    try {
      const results = await Promise.all(
        Object.entries(payloadByRegion).map(([region, body]) => scsBulkedit(
          this.props.userInfo.accessToken,
          body,
          region,
        ).then((response) => ({ region, response }))),
      );

      const failedRegions = results
        .filter(({ response }) => (response?.body?.failure?.length || 0) > 0)
        .map(({ region }) => region);

      if (failedRegions.length > 0) {
        this.setState({
          saveStatus: {
            isError: true,
            message: `Save failed for region${failedRegions.length === 1 ? '' : 's'}: ${failedRegions.join(', ')}.`,
          },
          saving: false,
        });
        return;
      }

      this.setState(
        {
          saveStatus: {
            isError: false,
            message: `Save succeeded (${totalKeys} keys across ${regionCount} region${regionCount === 1 ? '' : 's'}).`,
          },
          saving: false,
        },
        () => {
          if (hasNextTab && onSaveAndNext) {
            onSaveAndNext();
          }
        },
      );
    } catch (err) {
      this.setState({
        saveStatus: {
          isError: true,
          message: `Save failed: ${err?.message || 'Unknown error'}`,
        },
        saving: false,
      });
    }
  };

// represent shared defaults that shouldn't be forced onto the new store's
handleTargetValueChange = (regionValue, key, newValue) => {
    this.setState((prevState) => {
      const regionConfig = [...(prevState.targetConfigs[regionValue] || [])];
      const idx = regionConfig.findIndex((item) => item.key === key);
      if (idx !== -1) {
        regionConfig[idx] = { ...regionConfig[idx], value: newValue };
      }
      return {
        approvals: { ...prevState.approvals, [`${regionValue}-${key}`]: false },
        targetConfigs: { ...prevState.targetConfigs, [regionValue]: regionConfig },
      };
    });
  };

// safe: unapproved rows are simply omitted from the SCS write.
hasAnyApproved = () => {
    const { targetConfigs, approvals } = this.state;
    return Object.entries(targetConfigs).some(([regionValue, items]) => (
      items.some((item) => (
        this.isRowApprovable(regionValue, item)
        && !!approvals[`${regionValue}-${item.key}`]
      ))
    ));
  };

// copied — higher-level (CHAIN / COUNTRY / REGION / etc.) source values
hasConfigs = () => Object.values(this.state.targetConfigs).some((c) => c.length > 0);

// target's existing config. Only source rows whose scope is STORE are
isEditable = (regionValue, item) => {
    if (item.missingFromDefinition) return false;
    const flagKey = `${regionValue}-${item.key}`;
    const originalScopeType = this.state.originalTargetSnapshots[flagKey]?.scopeType ?? item.scopeType;
    if (originalScopeType === 'STORE') return true;
    // Once the user has committed the scope override for a non-STORE row,
    // the row behaves like a normal STORE-scoped row: its value stays
    // directly editable (no more Edit -> Done round-trip, no gray readonly
    // styling) until the user reloads or picks a different store.
    if (this.state.rowCommitted[flagKey]) return true;
    return !!this.state.rowEditMode[flagKey];
  };

// For COPY mode: overlay source values (forced to STORE scope) on top of the
isRowApprovable = (regionValue, item) => {
    if (item.missingFromDefinition) return false;
    const flagKey = `${regionValue}-${item.key}`;
    const originalScopeType = this.state.originalTargetSnapshots[flagKey]?.scopeType ?? item.scopeType;
    if (originalScopeType === 'STORE') return true;
    return !!this.state.rowCommitted[flagKey];
  };

// COMPARE-only: true when the reference-store value for `item.key` in
// `regionValue` differs from the target-store's current value. Rows without
// a definition in the region are treated as not-different so they don't
// leak into the "Show only differences" view. Mirrors the value
// normalization used in renderKeyRow so the highlight and the filter agree.
isRowDifferent = (regionValue, item) => {
    if (item.missingFromDefinition) return false;
    const sourceItems = this.state.sourceConfigs[regionValue] || [];
    const sourceItem = sourceItems.find((s) => s.key === item.key);
    const sourceValue = sourceItem && sourceItem.value !== '' && sourceItem.value != null
      ? String(sourceItem.value)
      : '';
    const targetValue = item.value === '' || item.value == null
      ? ''
      : String(item.value);
    return sourceValue !== targetValue;
  };

// have been committed via the Edit -> Done flow.
isRowModified = (regionValue, item) => {
    if (item.missingFromDefinition) return false;
    const flagKey = `${regionValue}-${item.key}`;
    const snap = this.state.originalTargetSnapshots[flagKey];
    if (!snap) return false;
    return (
      String(snap.value) !== String(item.value)
      || snap.scope !== item.scope
      || snap.scopeType !== item.scopeType
    );
  };

// rendered — for both directly-editable STORE rows and non-STORE rows that
loadConfigs = async () => {
    const {
      loadFailed, mode, sourceStoreKey, selectedTargetStore,
    } = this.state;
    const needsSource = this.requiresSourceStore();

    // Once any region has failed, the tab is dead until the user reloads
    // the page. We deliberately don't retry on subsequent store/workflow
    // selections — the banner already tells the user what to do.
    if (loadFailed) return;
    if (!selectedTargetStore) return;
    if (needsSource && !sourceStoreKey) return;

    this.setState({
      approvals: {},
      collapsedGroups: {},
      fetching: true,
      originalTargetSnapshots: {},
      rowCommitted: {},
      rowEditBackup: {},
      rowEditMode: {},
      saveStatus: null,
      sourceConfigs: {},
      targetConfigs: {},
    });

    try {
      const targetStoreKey = `${selectedTargetStore.address.country}-${selectedTargetStore.storeNumber}`;
      const defMapByRegion = await this.fetchDefinitions();
      const sourceConfigs = {};
      const targetConfigs = {};
      const defsFor = (region) => defMapByRegion[region] || {};

      if (mode === 'COPY') {
        const [sourceResults, targetResults] = await Promise.all([
          this.fetchRegionConfigsForStore(sourceStoreKey),
          this.fetchRegionConfigsForStore(targetStoreKey),
        ]);
        const targetByRegion = new Map(targetResults.map((r) => [r.region, r]));
        sourceResults.forEach(({ region, data }) => {
          const defMap = defsFor(region);
          const sourceRelevant = this.enrichFetched(data, defMap);
          const tgt = targetByRegion.get(region);
          const targetRelevant = this.enrichFetched(tgt?.data || [], defMap);
          const newConfig = this.buildCopyTargetConfig(sourceRelevant, targetRelevant, defMap);
          sourceConfigs[region] = targetRelevant;
          targetConfigs[region] = newConfig;
        });
      } else if (mode === 'QUICK_EDIT') {
        const regionResults = await this.fetchRegionConfigsForStore(targetStoreKey);
        regionResults.forEach(({ region, data }) => {
          const defMap = defsFor(region);
          const relevant = this.enrichFetched(data, defMap);
          sourceConfigs[region] = [];
          targetConfigs[region] = this.buildTargetFromAllowed(relevant, defMap);
        });
      } else {
        const [sourceResults, targetResults] = await Promise.all([
          this.fetchRegionConfigsForStore(sourceStoreKey),
          this.fetchRegionConfigsForStore(targetStoreKey),
        ]);
        const targetByRegion = new Map(targetResults.map((r) => [r.region, r]));
        sourceResults.forEach(({ region, data }) => {
          const defMap = defsFor(region);
          sourceConfigs[region] = this.enrichFetched(data, defMap);
          const tgt = targetByRegion.get(region);
          const targetRelevant = this.enrichFetched(tgt?.data || [], defMap);
          targetConfigs[region] = this.buildTargetFromAllowed(targetRelevant, defMap);
        });
      }

      const originalTargetSnapshots = this.buildOriginalSnapshots(targetConfigs);
      this.setState({
        fetching: false,
        originalTargetSnapshots,
        sourceConfigs,
        targetConfigs,
      });
    } catch (err) {
      // Pass all or fail all: any region (definition or config) failure
      // wipes any partial state we may have built and flips loadFailed so
      // no further attempts run until the page is refreshed.
      this.setState({
        fetching: false,
        loadFailed: true,
        sourceConfigs: {},
        targetConfigs: {},
      });
    }
  };

// snapshot we captured at load time. Drives whether the Undo affordance is
requiresSourceStore = () => {
    const { mode } = this.state;
    return mode === 'COPY' || mode === 'COMPARE';
  };

// True when the row's current scope/scopeType/value has drifted from the
resetFlowState = (extra = {}) => {
    this.setState({
      approvals: {},
      collapsedGroups: {},
      originalTargetSnapshots: {},
      rowCommitted: {},
      rowEditBackup: {},
      rowEditMode: {},
      saveStatus: null,
      sourceConfigs: {},
      targetConfigs: {},
      ...extra,
    });
  };

// otherwise lets scrolling rows flicker through the seam on Retina /
  resetForCountryChange = () => {
    this.setState({
      approvals: {},
      collapsedGroups: {},
      originalTargetSnapshots: {},
      rowCommitted: {},
      rowEditBackup: {},
      rowEditMode: {},
      saveStatus: null,
      saving: false,
      selectedSourceStore: null,
      selectedTargetStore: null,
      sourceConfigs: {},
      sourceStoreKey: '',
      targetConfigs: {},
    });
  };

selectSourceStore = (selectedSourceStore) => {
    this.setState(
      {
        approvals: {},
        originalTargetSnapshots: {},
        rowCommitted: {},
        rowEditBackup: {},
        rowEditMode: {},
        saveStatus: null,
        selectedSourceStore,
        sourceConfigs: {},
        sourceStoreKey: `${selectedSourceStore.address.country}-${selectedSourceStore.storeNumber}`,
        targetConfigs: {},
      },
      () => {
        if (this.state.selectedTargetStore) this.loadConfigs();
      },
    );
  };

selectTargetStore = (selectedTargetStore) => {
    this.setState(
      {
        approvals: {},
        originalTargetSnapshots: {},
        rowCommitted: {},
        rowEditBackup: {},
        rowEditMode: {},
        saveStatus: null,
        selectedTargetStore,
        sourceConfigs: {},
        targetConfigs: {},
      },
      () => this.loadConfigs(),
    );
  };

selectWorkflow = (mode) => {
    if (mode === this.state.mode) return;

    this.resetFlowState({
      keySearch: '',
      mode,
      selectedSourceStore: null,
      selectedTargetStore: null,
      showOnlyDifferences: false,
      sourceStoreKey: '',
    });
  };

showSourceColumn = () => this.requiresSourceStore();

toggleGroupCollapsed = (groupName) => {
    this.setState((prev) => ({
      collapsedGroups: {
        ...prev.collapsedGroups,
        [groupName]: !prev.collapsedGroups[groupName],
      },
    }));
  };

// fractional-zoom displays.
  updateStickyOffsets = () => {
    const panel = this.panelRef.current;
    if (!panel) return;
    const measure = (el) => (el ? Math.floor(el.getBoundingClientRect().height) : 0);
    const tabsH = measure(this.regionTabsRef.current);
    const theadH = measure(this.tableHeadRef.current);
    panel.style.setProperty('--soa-region-tabs-h', `${tabsH}px`);
    panel.style.setProperty('--soa-thead-h', `${theadH}px`);
  };

  // Build a row for a key that has no definition in the region's defMap. SCS
  // won't return a value for a key that isn't defined, so these rows always
  // start empty. Surfaced purely so users can see that the app expects the

  // Group items by their `group` field (from the region's definitions map, or
  // 'Other Settings' / 'Missing Definitions' fallbacks). Returns an ordered
  // list of [groupName, items[]] tuples so the caller can render them as
  // collapsible sections while preserving a stable display order. 'Other
  // Settings' and 'Missing Definitions' are pushed to the end because they are

  renderScopePill = (scopeType, { transitioning = false } = {}) => {
    const label = scopeType || 'STORE';
    if (transitioning) {
      return (
        <span
          className="soa-scope-pill soa-scope-pill-transition"
          title="Scope will change to STORE on save"
        >
          <span className="soa-scope-pill-label">SCOPE</span>
          <span className="soa-scope-pill-from">{label}</span>
          <span className="soa-scope-pill-arrow">&rarr;</span>
          <span className="soa-scope-pill-to">STORE</span>
        </span>
      );
    }
    return (
      <span className="soa-scope-pill">
        <span className="soa-scope-pill-label">SCOPE</span>
        <span className="soa-scope-pill-value">{label}</span>
      </span>
    );
  };

  // Restore a row to the exact scope/scopeType/value we loaded from SCS,
  // regardless of what the user changed since. Also wipes the edit-flow
  // bookkeeping (rowCommitted / rowEditMode / rowEditBackup) and clears any
  // approval, so a Undo'd row behaves as if it had never been touched — the

  renderTargetValueInput = (selectedRegion, item) => {
    const valueType = (item.valueType || 'STRING').toUpperCase();
    const currentValue = item.value === '' || item.value == null ? '' : String(item.value);
    if (valueType === 'BOOLEAN') {
      return (
        <select
          className="soa-value-input"
          value={currentValue}
          onChange={(e) => this.handleTargetValueChange(selectedRegion, item.key, e.target.value)}
        >
          <option value="">Select</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      );
    }
    return (
      <input
        className="soa-value-input"
        type={valueType === 'INTEGER' ? 'number' : 'text'}
        value={currentValue}
        onChange={(e) => this.handleTargetValueChange(selectedRegion, item.key, e.target.value)}
      />
    );
  };

  renderColumnHeaderContent = (label, storeHeader) => (
    <div className="soa-th-label-stack">
      <span className="soa-th-label">{label}</span>
      {storeHeader && (
        <span className="soa-th-store">
          <span className="soa-th-store-name" title={storeHeader.name}>
            {storeHeader.name}
          </span>
          {storeHeader.meta && (
            <span className="soa-th-store-meta">{storeHeader.meta}</span>
          )}
        </span>
      )}
    </div>
  );

  renderKeyRow = (item, globalIdx, selectedRegion, showSourceColumn, sourceForRegion) => {
    const {
      approvals, mode, rowEditMode, rowCommitted, originalTargetSnapshots,
    } = this.state;
    const isMissingDef = !!item.missingFromDefinition;
    const sourceItem = showSourceColumn
      ? sourceForRegion.find((s) => s.key === item.key)
      : null;
    const editable = this.isEditable(selectedRegion, item);
    const flagKey = `${selectedRegion}-${item.key}`;
    const rowInEditMode = !!rowEditMode[flagKey];
    const rowIsCommitted = !!rowCommitted[flagKey];
    const originalScopeType = originalTargetSnapshots[flagKey]?.scopeType ?? item.scopeType;
    // Show the Edit toggle only for non-STORE rows that haven't been
    // committed yet. Once committed, the row is rendered as a normal
    // editable STORE row, so an inline Edit button would be redundant.
    const showEditToggle = !isMissingDef
      && originalScopeType
      && originalScopeType !== 'STORE'
      && !rowIsCommitted;
    const showScopeTransition = showEditToggle && rowInEditMode && !rowIsCommitted;
    const snap = originalTargetSnapshots[flagKey];
    const valueChanged = !!snap && String(snap.value) !== String(item.value);
    // Undo only appears when the row has actually drifted from its loaded
    // state, and never while the user is mid-edit on a non-STORE row (Cancel
    // already covers that flow). Because Edit is only rendered for
    // uncommitted non-STORE rows — which cannot yet differ from the snapshot
    // — Edit and Undo are mutually exclusive by construction.
    const showUndo = !isMissingDef
      && !rowInEditMode
      && this.isRowModified(selectedRegion, item);
    const approvalDisabled = !this.isRowApprovable(selectedRegion, item) || rowInEditMode;
    const hasSourceItem = !!sourceItem;
    const normalizedSourceValue = hasSourceItem
      && sourceItem.value !== '' && sourceItem.value != null
      ? String(sourceItem.value)
      : '';
    const normalizedTargetValue = item.value === '' || item.value == null
      ? ''
      : String(item.value);
    const sourceValue = normalizedSourceValue || EMPTY_VALUE_PLACEHOLDER;
    const valuesDiffer = mode === 'COMPARE'
      && !isMissingDef
      && normalizedSourceValue !== normalizedTargetValue;
    const rowClasses = [
      globalIdx % 2 === 0 ? 'soa-row-even' : '',
      isMissingDef ? 'soa-row-missing-def' : '',
      valuesDiffer ? 'soa-row-differs' : '',
    ].filter(Boolean).join(' ');

    return (
      <tr key={item.key} className={rowClasses}>
        <td className="soa-row-index">{globalIdx + 1}</td>
        <td>
          <button
            className="soa-key-name"
            type="button"
            onBlur={this.handleKeyHoverLeave}
            onClick={(e) => this.handleKeyHoverEnter(item, e)}
            onFocus={(e) => this.handleKeyHoverEnter(item, e)}
            onMouseEnter={(e) => this.handleKeyHoverEnter(item, e)}
            onMouseLeave={this.handleKeyHoverLeave}
          >
            {item.key}
          </button>
        </td>
        {showSourceColumn && (
          <td className="soa-td-value soa-source-value">
            {isMissingDef && (
              <span className="soa-missing-def-message">
                Key not defined in this region
              </span>
            )}
            {!isMissingDef && hasSourceItem && (
              <div className="soa-cell-stack">
                {this.renderScopePill(sourceItem.scopeType)}
                <span
                  className="soa-value-readonly soa-value-source"
                  title={String(sourceItem.value)}
                >
                  {sourceValue}
                </span>
              </div>
            )}
            {!isMissingDef && !hasSourceItem && (
              <span aria-label="Not set" className="soa-value-readonly">
                {EMPTY_VALUE_PLACEHOLDER}
              </span>
            )}
          </td>
        )}
        <td className="soa-td-value">
          {isMissingDef ? (
            <span className="soa-missing-def-message">
              Key not defined in this region
            </span>
          ) : (
            <div className="soa-cell-stack">
              <div className="soa-cell-pills">
                {this.renderScopePill(
                  showScopeTransition ? originalScopeType : item.scopeType,
                  { transitioning: showScopeTransition },
                )}
                {item.missingFromTarget && (
                  <span
                    className="soa-missing-config-pill"
                    title="No config for this key on the target store"
                  >
                    Missing Config
                  </span>
                )}
              </div>
              <div className="soa-target-cell">
                {editable ? (
                  this.renderTargetValueInput(selectedRegion, item)
                ) : (
                  <span className="soa-value-readonly">
                    {item.value === '' || item.value == null
                      ? EMPTY_VALUE_PLACEHOLDER
                      : String(item.value)}
                  </span>
                )}
                {showEditToggle && (
                  rowInEditMode ? (
                    <div className="soa-row-edit-actions">
                      <button
                        aria-label="Done"
                        className="soa-row-edit-toggle soa-row-edit-toggle-icon soa-row-edit-toggle-done"
                        disabled={!valueChanged}
                        title="Done"
                        type="button"
                        onClick={() => this.handleRowEditDone(selectedRegion, item.key)}
                      >
                        <span aria-hidden="true">&#10003;</span>
                      </button>
                      <button
                        aria-label="Cancel"
                        className="soa-row-edit-toggle soa-row-edit-toggle-icon soa-row-edit-toggle-cancel"
                        title="Cancel"
                        type="button"
                        onClick={() => this.handleRowEditCancel(selectedRegion, item.key)}
                      >
                        <span aria-hidden="true">&#10005;</span>
                      </button>
                    </div>
                  ) : (
                    <button
                      aria-label="Edit"
                      className="soa-row-edit-toggle soa-row-edit-toggle-icon"
                      title="Edit"
                      type="button"
                      onClick={() => this.handleRowEditStart(selectedRegion, item.key)}
                    >
                      <span aria-hidden="true">&#9998;</span>
                    </button>
                  )
                )}
                {showUndo && (
                  <button
                    aria-label="Undo"
                    className="soa-row-edit-toggle soa-row-edit-toggle-icon soa-row-edit-toggle-undo"
                    title="Undo — restore original scope and value"
                    type="button"
                    onClick={() => this.handleRowUndo(selectedRegion, item.key)}
                  >
                    <span aria-hidden="true">&#8634;</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </td>
        <td className="soa-approve-cell">
          <input
            aria-label={`Approve ${item.key}`}
            checked={!!approvals[flagKey]}
            disabled={approvalDisabled}
            title={isMissingDef ? 'Cannot approve: no definition for this key in this region' : undefined}
            type="checkbox"
            onChange={() => this.handleApprovalChange(selectedRegion, item.key)}
          />
        </td>
      </tr>
    );
  };

  renderGroupedTableBody = (targetConfig, selectedRegion, showSourceColumn, sourceForRegion, columnCount) => {
    const {
 approvals, collapsedGroups, keySearch, targetConfigs
} = this.state;

    if (targetConfig.length === 0) {
      return (
        <tr><td className="soa-empty" colSpan={columnCount}>No config keys found</td></tr>
      );
    }

    // When the user is actively searching, we force every group open so
    // matches are always visible without them having to expand each group
    // manually.
    const forceExpand = !!keySearch;
    const groups = this.getGroupedTargetConfig(targetConfig);

    // Group-header approval counts (and the group-level "Approve all"
    // checkbox) must reflect the whole group, not just the search-visible
    // subset — otherwise typing in the search box would appear to change
    // approval progress. We recompute groups from the pre-search list
    // (still respecting the Key Group selector) and look up the full item
    // set by group name.
    const preSearchRegionItems = this.getFilteredConfigs(
      targetConfigs, { skipSearch: true },
    )[selectedRegion] || [];
    const preSearchGroups = new Map(this.getGroupedTargetConfig(preSearchRegionItems));

    const rows = [];
    let globalIdx = 0;
    groups.forEach(([groupName, groupItems]) => {
      const isCollapsed = !forceExpand && !!collapsedGroups[groupName];
      const fullGroupItems = preSearchGroups.get(groupName) || groupItems;
      const approvableInGroup = fullGroupItems.filter(
        (item) => this.isRowApprovable(selectedRegion, item),
      );
      const approvedInGroup = approvableInGroup.filter(
        (item) => approvals[`${selectedRegion}-${item.key}`],
      ).length;
      const groupAllApproved = approvableInGroup.length > 0
        && approvedInGroup === approvableInGroup.length;
      const keyLabel = fullGroupItems.length === 1 ? 'key' : 'keys';

      rows.push(
        <tr key={`group-header-${groupName}`} className="soa-group-header-row">
          <td className="soa-group-header-cell" colSpan={columnCount - 1}>
            <button
              aria-expanded={!isCollapsed}
              className={`soa-group-header-button ${isCollapsed ? 'soa-group-collapsed' : ''}`}
              disabled={forceExpand}
              title={forceExpand ? 'Groups are auto-expanded while filtering' : undefined}
              type="button"
              onClick={() => this.toggleGroupCollapsed(groupName)}
            >
              <span aria-hidden="true" className="soa-group-chevron">&#9656;</span>
              <span className="soa-group-name">{groupName}</span>
              <span className="soa-group-count">{`${fullGroupItems.length} ${keyLabel}`}</span>
              {approvableInGroup.length > 0 && (
                <span className={`soa-group-badge ${groupAllApproved ? 'soa-group-badge-approved' : ''}`}>
                  {`${approvedInGroup}/${approvableInGroup.length} approved`}
                </span>
              )}
            </button>
          </td>
          <td className="soa-group-header-approve">
            {approvableInGroup.length > 0 && (
              <input
                checked={groupAllApproved}
                title={`Approve all ${approvableInGroup.length} approvable ${approvableInGroup.length === 1 ? 'key' : 'keys'} in ${groupName}`}
                type="checkbox"
                onChange={() => this.handleGroupApproveAll(selectedRegion, fullGroupItems)}
              />
            )}
          </td>
        </tr>,
      );

      if (!isCollapsed) {
        groupItems.forEach((item) => {
          rows.push(this.renderKeyRow(item, globalIdx, selectedRegion, showSourceColumn, sourceForRegion));
          globalIdx += 1;
        });
      }
    });

    return rows;
  };

  renderCombinedPanel = () => {
    const {
      approvals, mode, selectedRegion, selectedSourceStore, selectedTargetStore,
      sourceConfigs, targetConfigs,
    } = this.state;
    const filteredSourceConfigs = this.getFilteredConfigs(sourceConfigs);
    const filteredTargetConfigs = this.getFilteredConfigs(
      targetConfigs, { applyApprovableFilter: true, applyDifferencesFilter: true },
    );

    const showSourceColumn = this.showSourceColumn();
    const sourceColumnLabel = this.getSourceColumnLabel();
    const targetColumnLabel = this.getTargetColumnLabel();

    // Store info is surfaced inline under each column label in flows that
    // compare or copy between two stores. Quick Edit only has the target
    // column and the store selector directly above already identifies it,
    // so we don't duplicate that here.
    //
    // COPY renders "Original Config" and "New Config" as the target store's
    // config before vs. after the source is overlaid (see loadConfigs) — the
    // source store is only a data provider, never rendered on its own — so
    // both column headers must identify the target store. COMPARE truly
    // shows two separate stores side-by-side, so source column identifies
    // the reference store and target column identifies the store to edit.
    let sourceStoreHeader = null;
    if (mode === 'COMPARE') {
      sourceStoreHeader = this.formatStoreForHeader(selectedSourceStore);
    } else if (mode === 'COPY') {
      sourceStoreHeader = this.formatStoreForHeader(selectedTargetStore);
    }
    const targetStoreHeader = mode === 'COPY' || mode === 'COMPARE'
      ? this.formatStoreForHeader(selectedTargetStore)
      : null;

    const targetConfig = filteredTargetConfigs[selectedRegion] || [];
    const sourceForRegion = filteredSourceConfigs[selectedRegion] || [];

    const approvableInRegion = targetConfig.filter(
      (item) => this.isRowApprovable(selectedRegion, item),
    );
    const allApproved = approvableInRegion.length > 0 && approvableInRegion.every(
      (item) => approvals[`${selectedRegion}-${item.key}`],
    );

    const columnCount = showSourceColumn ? 5 : 4;

    return (
      <section ref={this.panelRef} className="soa-panel soa-combined-panel">
        <div ref={this.regionTabsRef} className="soa-region-tabs">
          {ALL_REGIONS.map((region) => {
            const regionTarget = targetConfigs[region.value] || [];
            const approvable = regionTarget.filter(
              (item) => this.isRowApprovable(region.value, item),
            );
            const approvedCount = approvable.filter(
              (item) => approvals[`${region.value}-${item.key}`],
            ).length;
            const regionApproved = approvable.length > 0 && approvedCount === approvable.length;
            return (
              <button
                key={region.value}
                className={`soa-region-tab ${selectedRegion === region.value ? 'soa-region-tab-active' : ''} ${regionApproved ? 'soa-region-tab-approved' : ''}`}
                type="button"
                onClick={() => this.setState({ selectedRegion: region.value })}
              >
                {region.label}
                <span className={`soa-tab-badge ${regionApproved ? 'soa-badge-approved' : ''}`}>
                  {approvedCount}/{approvable.length} approved
                </span>
              </button>
            );
          })}
        </div>
        <table className="soa-table">
          <thead ref={this.tableHeadRef}>
            <tr>
              <th className="soa-th-index">#</th>
              <th className="soa-th-key">Key</th>
              {showSourceColumn && (
                <th className="soa-th-value soa-th-source-value">
                  {this.renderColumnHeaderContent(sourceColumnLabel, sourceStoreHeader)}
                </th>
              )}
              <th className="soa-th-value soa-th-target-value">
                {this.renderColumnHeaderContent(targetColumnLabel, targetStoreHeader)}
              </th>
              <th className="soa-th-approve">
                <label className="soa-checkbox-label">
                  <input
                    checked={allApproved}
                    type="checkbox"
                    onChange={() => this.handleApproveAllForRegion(selectedRegion)}
                  />
                  All
                </label>
              </th>
            </tr>
          </thead>
          <tbody>
            {this.renderGroupedTableBody(
              targetConfig,
              selectedRegion,
              showSourceColumn,
              sourceForRegion,
              columnCount,
            )}
          </tbody>
        </table>
      </section>
    );
  };

  renderModeToggles = () => {
    const { mode } = this.state;

    const workflows = [
      { label: 'Quick Edit', mode: 'QUICK_EDIT' },
      { label: 'Copy from Store', mode: 'COPY' },
      { label: 'Compare & Edit', mode: 'COMPARE' },
    ];

    const activeIndex = workflows.findIndex((w) => w.mode === mode);
    const safeIndex = activeIndex >= 0 ? activeIndex : 0;

    return (
      <section aria-label="Workflow" className="soa-workflow-section">
        <div aria-label="Workflow" className="soa-segmented" role="radiogroup">
          <span
            aria-hidden="true"
            className="soa-segmented-indicator"
            style={{ transform: `translateX(${safeIndex * 100}%)` }}
          />
          {workflows.map((w, i) => {
            const isActive = i === safeIndex;
            return (
              <button
                key={w.mode}
                aria-checked={isActive}
                className={`soa-segmented-btn ${isActive ? 'soa-segmented-btn-active' : ''}`}
                role="radio"
                type="button"
                onClick={() => this.selectWorkflow(w.mode)}
              >
                <span className="soa-segmented-label">{w.label}</span>
              </button>
            );
          })}
        </div>
      </section>
    );
  };

  renderStoreSelectors = () => {
    const { selectedCountry } = this.props;
    const {
      mode,
      selectedSourceStore,
      selectedTargetStore,
    } = this.state;

    const labels = this.getSelectorLabels();
    const showSource = this.requiresSourceStore();
    // Only COPY is directional (source → target), so the arrow badge is
    // meaningful there. COMPARE just shows the two stores side-by-side, so
    // we drop the arrow but keep the divider spacer for consistent layout.
    const showArrow = mode === 'COPY';

    return (
      <section className="soa-selectors">
        {showSource && (
          <>
            <article className="soa-selector-card">
              <div className="soa-selector-header">
                <span className="soa-selector-badge">{labels.source}</span>
              </div>
              <div className="soa-selector-body">
                <div className="soa-store-field">
                  <StoreSelect
                    fullWidth
                    countryRestriction={selectedCountry || {}}
                    selectCountry={() => {}}
                    selectStore={this.selectSourceStore}
                    selectedCountry={selectedCountry}
                    selectedStore={selectedSourceStore}
                  />
                </div>
              </div>
            </article>
            <div className="soa-arrow-divider">
              {showArrow && <span className="soa-arrow">&rarr;</span>}
            </div>
          </>
        )}
        <article className="soa-selector-card">
          <div className="soa-selector-header">
            <span className="soa-selector-badge soa-selector-badge-target">{labels.target}</span>
          </div>
          <div className="soa-selector-body">
            <div className="soa-store-field">
              <StoreSelect
                fullWidth
                countryRestriction={selectedCountry || {}}
                selectCountry={() => {}}
                selectStore={this.selectTargetStore}
                selectedCountry={selectedCountry}
                selectedStore={selectedTargetStore}
              />
            </div>
          </div>
        </article>
      </section>
    );
  };

  render = () => {
    const {
      fetching,
      keySearch,
      loadFailed,
      mode,
      saving,
      selectedKeyGroup,
      showOnlyApprovable,
      showOnlyDifferences,
    } = this.state;

    const hasConfigs = this.hasConfigs();
    const canSave = this.hasAnyApproved() && !saving;

    return (
      <>
        {this.renderModeToggles()}
        {this.renderStoreSelectors()}

        {loadFailed && (
          <aside className="m4-sm text-color-error body-2">Please try again.</aside>
        )}

        {fetching && <Loading className="mt-10-sm" />}

        {hasConfigs && !fetching && (
          <>
            <section className="soa-toolbar">
              <div className="soa-search-box">
                <input
                  className="soa-search-input"
                  placeholder="Search keys..."
                  type="text"
                  value={keySearch}
                  onChange={(e) => this.setState({ keySearch: e.target.value })}
                />
                {keySearch && (
                  <button
                    className="soa-search-clear"
                    type="button"
                    onClick={() => this.setState({ keySearch: '' })}
                  >
                    &times;
                  </button>
                )}
              </div>
              <div className="soa-filter-group">
                <label className="soa-filter-label" htmlFor="soa-key-group-select">Key Group:</label>
                <select
                  className="soa-filter-select"
                  id="soa-key-group-select"
                  value={selectedKeyGroup}
                  onChange={(e) => this.setState({ selectedKeyGroup: e.target.value })}
                >
                  {KEY_GROUP_FILTERS.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>
              <label className="soa-approvable-filter">
                <input
                  checked={showOnlyApprovable}
                  type="checkbox"
                  onChange={(e) => this.setState({ showOnlyApprovable: e.target.checked })}
                />
                Show only approvable keys
              </label>
              {mode === 'COMPARE' && (
                <label className="soa-approvable-filter">
                  <input
                    checked={showOnlyDifferences}
                    type="checkbox"
                    onChange={(e) => this.setState({ showOnlyDifferences: e.target.checked })}
                  />
                  Show only differences
                </label>
              )}
            </section>

            <section className="soa-panels-container">
              {this.renderCombinedPanel()}
            </section>

            <section className="soa-status-bar">
              {/* <p className={`soa-status-message ${allApproved ? 'soa-status-success' : 'soa-status-warning'}`}>
                {allApproved
                  ? 'All config values approved. Ready to save.'
                  : 'Approve all config values across all regions to enable save.'}
              </p> */}
              {this.state.saveStatus && (
                <p className={`soa-status-message ${this.state.saveStatus.isError ? 'soa-status-warning' : 'soa-status-success'}`}>
                  {this.state.saveStatus.message}
                </p>
              )}
            </section>

            <section className="ncss-row mt1-sm mb8-sm">
              <article className="ncss-col-sm-12 ta-sm-c">
                <ButtonBlack
                  isDisabled={!canSave}
                  label={this.props.hasNextTab ? 'Save and Continue' : 'Save'}
                  onClick={this.handleSave}
                />
                {saving && <Loading className="mt2-sm" />}
              </article>
            </section>
          </>
        )}
        {this.state.hoveredKeyInfo && (
          <div
            className="soa-key-tooltip"
            role="tooltip"
            style={{ left: this.state.hoveredKeyInfo.left, top: this.state.hoveredKeyInfo.top }}
          >
            <div className="soa-key-tooltip-key">
              {this.state.hoveredKeyInfo.key}
              {this.state.hoveredKeyInfo.valueType && (
                <span className="soa-key-tooltip-type">
                  {' '}[{this.state.hoveredKeyInfo.valueType}]
                </span>
              )}
            </div>
            {this.state.hoveredKeyInfo.description && (
              <div className="soa-key-tooltip-desc">{this.state.hoveredKeyInfo.description}</div>
            )}
          </div>
        )}
      </>
    );
  };
}

StoreConfigTab.defaultProps = {
  hasNextTab: false,
  onSaveAndNext: null,
  selectedCountry: null,
};

StoreConfigTab.propTypes = {
  hasNextTab: PropTypes.bool,
  onSaveAndNext: PropTypes.func,
  selectedCountry: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  userInfo: PropTypes.shape({
    accessToken: PropTypes.string,
    email: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

export default StoreConfigTab;
